import React, { useState, useRef, useEffect } from 'react';
import * as styles from '../styles/components/editable-title.css.ts';

interface EditableTitleProps {
  title: string;
  onTitleChange: (newTitle: string) => void;
  placeholder?: string;
  className?: string;
  style?: React.CSSProperties;
}

export function EditableTitle({ 
  title, 
  onTitleChange, 
  placeholder = 'Click to add title...',
  className = '',
  style = {}
}: EditableTitleProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [editValue, setEditValue] = useState(title);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    setEditValue(title);
  }, [title]);

  useEffect(() => {
    if (isEditing && inputRef.current) {
      inputRef.current.focus();
      inputRef.current.select();
    }
  }, [isEditing]);

  const handleClick = () => {
    setIsEditing(true);
  };

  const handleBlur = () => {
    handleSave();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleSave();
    } else if (e.key === 'Escape') {
      e.preventDefault();
      handleCancel();
    }
  };

  const handleSave = () => {
    const trimmedValue = editValue.trim();
    if (trimmedValue !== title) {
      onTitleChange(trimmedValue);
    }
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditValue(title);
    setIsEditing(false);
  };

  if (isEditing) {
    return (
      <input
        ref={inputRef}
        type="text"
        value={editValue}
        onChange={(e) => setEditValue(e.target.value)}
        onBlur={handleBlur}
        onKeyDown={handleKeyDown}
        className={`${styles.editableInput} ${className}`}
        style={style}
      />
    );
  }

  return (
    <div
      onClick={handleClick}
      className={`${styles.editableTitle} ${className}`}
      style={style}
      title="Click to edit"
    >
      {title || placeholder}
    </div>
  );
}